import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { preventAutoCloseTriggerFocus } from '@prophecy/utils/dom';
import { useFocusTrap } from '@prophecy/utils/react/focus';
import utilify from '@prophecy/utils/react/utilify';
import { Close as RadixClose, Overlay, Portal, Root, Trigger } from '@radix-ui/react-dialog';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { AlertTriangleIcon, CheckCircleBrokenIcon, InfoCircleIcon, XCloseIcon } from '../Icons';
import { Stack } from '../Layout';
import { BaseColor, theme } from '../theme';
import { Text } from '../Typography/Text';
import { CancelButton, OkButton, StyledChildrenWrap, StyledContent, StyledDialog, StyledFooter, StyledIconContainer, StyledLabel, StyledOverlay } from './styled';
import { DialogComponentNames, tokens } from './tokens';
import { DialogSize, IconPlacement } from './types';
const Close = RadixClose;
const StyledCrossIcon = styled(Button)`
  position: absolute;
  top: ${theme.spaces.x20};
  right: ${theme.spaces.x20};
`;
//Exposing `DialogTitleContainer` for nested style overrides of the title container.
export const DialogTitleContainer = styled(Stack)``;
const DialogContent = /*#__PURE__*/ React.forwardRef(({ size, footer, overlayClassName, children, open, showCloseIcon, rootClassName, container, ...props }, forwardedRef)=>{
    return /*#__PURE__*/ _jsx(Portal, {
        children: /*#__PURE__*/ _jsxs(Overlay, {
            className: rootClassName,
            children: [
                /*#__PURE__*/ _jsx(StyledOverlay, {
                    "data-component": DialogComponentNames.overlay,
                    className: overlayClassName,
                    "data-state": open ? 'open' : 'closed'
                }),
                /*#__PURE__*/ _jsxs(StyledDialog, {
                    "data-component": DialogComponentNames.content,
                    ...props,
                    size: size,
                    ref: forwardedRef,
                    children: [
                        /*#__PURE__*/ _jsxs(Stack, {
                            direction: "vertical",
                            height: "100%",
                            align: "space-between",
                            alignY: "stretch",
                            children: [
                                /*#__PURE__*/ _jsx(StyledContent, {
                                    grow: "1",
                                    ref: container,
                                    tabIndex: -1,
                                    children: children
                                }),
                                footer && /*#__PURE__*/ _jsx(StyledFooter, {
                                    children: footer
                                })
                            ]
                        }),
                        showCloseIcon && /*#__PURE__*/ _jsx(Close, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsx(StyledCrossIcon, {
                                "aria-label": "close-dialog",
                                icon: /*#__PURE__*/ _jsx(XCloseIcon, {
                                    type: "default"
                                }),
                                variant: "tertiaryGrey",
                                shape: "circle",
                                size: "m"
                            })
                        })
                    ]
                })
            ]
        })
    });
});
export const IconMap = {
    Warning: AlertTriangleIcon,
    Alert: AlertTriangleIcon,
    Info: InfoCircleIcon,
    Success: CheckCircleBrokenIcon
};
const AlignMap = {
    center: 'center',
    left: 'start',
    right: 'space-between'
};
export const Dialog = ({ size = DialogSize.m, children, trigger, closeButton = {}, okButton = {}, ButtonsContainer, footer, closable = true, open, onClose, icon, onOk, iconPlacement = IconPlacement.left, title, subTitle, overlayClassName, showCloseIcon, className, rootClassName, isBlocking, onCloseAutoFocus, ...restProps })=>{
    useEffect(()=>{
        if (!isBlocking) return;
        document.body.dataset.hasBlockingDialog = '1';
        return ()=>{
            delete document.body.dataset.hasBlockingDialog;
        };
    }, [
        isBlocking
    ]);
    const contentContainer = useFocusTrap(Boolean(open));
    const okButtonRef = useRef(null);
    const cancelButtonRef = useRef(null);
    useEffect(()=>{
        const containerElm = contentContainer.current;
        if (!containerElm || !open) return;
        if (!okButtonRef.current || !cancelButtonRef.current) return;
        // focus on primary dialog button if the content doesn't have any input elements
        if (!containerElm.querySelector('input, select, textarea')) {
            const button = okButtonRef.current || cancelButtonRef.current;
            button.focus();
        }
    }, [
        contentContainer,
        open
    ]);
    const onOpenChange = (open)=>{
        if (!open && closable) {
            onClose?.();
        }
    };
    const isBlock = Boolean(closeButton?.block || okButton?.block);
    //button block property overrieds some of the required styles, override to false in button props.
    const buttons = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            okButton && /*#__PURE__*/ _jsx(OkButton, {
                variant: "primary",
                ...okButton,
                ref: okButtonRef,
                block: false,
                onClick: onOk,
                isBlock: isBlock,
                children: okButton.children ? okButton.children : 'OK'
            }),
            closeButton && /*#__PURE__*/ _jsx(Close, {
                asChild: true,
                children: /*#__PURE__*/ _jsx(CancelButton, {
                    variant: "secondaryGrey",
                    ...closeButton,
                    ref: cancelButtonRef,
                    block: false,
                    isBlock: isBlock,
                    children: closeButton.children ? closeButton.children : 'Cancel'
                })
            })
        ]
    });
    const buttonWrapper = ButtonsContainer ? /*#__PURE__*/ _jsx(ButtonsContainer, {
        children: buttons
    }) : /*#__PURE__*/ _jsx(Stack, {
        direction: "horizontal",
        gap: theme.spaces.x12,
        align: "end",
        alignY: "center",
        children: buttons
    });
    return /*#__PURE__*/ _jsxs(Root, {
        open: open,
        ...restProps,
        onOpenChange: onOpenChange,
        children: [
            trigger && /*#__PURE__*/ _jsx(Trigger, {
                asChild: true,
                children: trigger
            }),
            /*#__PURE__*/ _jsxs(DialogContent, {
                className: className,
                rootClassName: rootClassName,
                overlayClassName: overlayClassName,
                size: size,
                footer: footer,
                open: Boolean(open),
                container: contentContainer,
                showCloseIcon: showCloseIcon,
                onCloseAutoFocus: onCloseAutoFocus || preventAutoCloseTriggerFocus,
                children: [
                    /*#__PURE__*/ _jsxs(Stack, {
                        direction: iconPlacement === IconPlacement.center ? 'vertical' : 'horizontal',
                        gap: theme.spaces.x24,
                        align: AlignMap[iconPlacement],
                        alignY: subTitle ? 'start' : 'center',
                        children: [
                            iconPlacement === 'left' || iconPlacement === 'center' ? icon : null,
                            /*#__PURE__*/ _jsxs(DialogTitleContainer, {
                                direction: "vertical",
                                gap: theme.spaces.x12,
                                align: "start",
                                alignY: "start",
                                children: [
                                    title && /*#__PURE__*/ _jsx(Text, {
                                        "data-component": DialogComponentNames.title,
                                        weight: tokens.Dialog.Content.headingFontWeight,
                                        tone: tokens.Dialog.Content.headingColor,
                                        level: "lg",
                                        children: title
                                    }),
                                    subTitle && /*#__PURE__*/ _jsx(StyledLabel, {
                                        "data-component": DialogComponentNames.subTitle,
                                        level: "sm",
                                        children: subTitle
                                    })
                                ]
                            }),
                            iconPlacement === 'right' ? icon : null
                        ]
                    }),
                    /*#__PURE__*/ _jsx(StyledChildrenWrap, {
                        children: children
                    }),
                    buttonWrapper
                ]
            })
        ]
    });
};
const dialog = utilify(({ props, render, unmount })=>{
    return new Promise((resolve)=>{
        const renderDialog = ({ children, ..._props })=>{
            const onOk = ()=>{
                _props.onOk?.();
                resolve(true);
                unmount();
            };
            const onClose = ()=>{
                _props.onClose?.();
                resolve(false);
                unmount();
            };
            const CustomDialog = props.Component || Dialog;
            render(/*#__PURE__*/ _jsx(CustomDialog, {
                ..._props,
                children: typeof children === 'function' ? children({
                    onOk,
                    onClose
                }) : children,
                open: true,
                onOk: onOk,
                onClose: onClose,
                isBlocking: true
            }), {
                unmountOnNavigation: true
            });
        };
        renderDialog(props);
    });
});
Dialog.alert = ({ iconPlacement = 'left', ...props })=>dialog({
        ...props,
        iconPlacement,
        icon: /*#__PURE__*/ _jsx(StyledIconContainer, {
            iconPlacement: iconPlacement,
            tone: BaseColor.error,
            children: /*#__PURE__*/ _jsx(IconMap.Alert, {
                type: "default"
            })
        })
    });
Dialog.success = ({ iconPlacement = 'left', ...props })=>dialog({
        iconPlacement,
        icon: /*#__PURE__*/ _jsx(StyledIconContainer, {
            iconPlacement: iconPlacement,
            tone: BaseColor.success,
            children: /*#__PURE__*/ _jsx(IconMap.Success, {
                type: "default"
            })
        }),
        closeButton: null,
        ...props
    });
Dialog.warning = ({ iconPlacement = 'left', ...props })=>dialog({
        iconPlacement,
        icon: /*#__PURE__*/ _jsx(StyledIconContainer, {
            iconPlacement: iconPlacement,
            tone: BaseColor.warning,
            children: /*#__PURE__*/ _jsx(IconMap.Warning, {
                type: "default"
            })
        }),
        ...props
    });
Dialog.info = ({ iconPlacement = 'left', closeButton = null, ...props })=>dialog({
        iconPlacement,
        icon: /*#__PURE__*/ _jsx(StyledIconContainer, {
            iconPlacement: iconPlacement,
            tone: BaseColor.primary,
            children: /*#__PURE__*/ _jsx(IconMap.Info, {
                type: "default"
            })
        }),
        closeButton,
        ...props
    });
Dialog.open = dialog;
export function isDialogOpened() {
    return Boolean(document.querySelector(`[data-radix-focus-guard]`) && document.querySelector('[role="dialog"]'));
}
